import { useState } from "react";

import { useTranslation } from "@impero-com/common/utils/i18n";
import {
    ChartLegend,
    ChartSwatch,
    EmptyState,
    GroupedBarChart,
    GroupedBarChartBar,
    GroupedBarChartBucket,
    PendoId,
    SegmentedControl,
    StackedBarTrendChart,
    StackedBarTrendChartBar,
    Svg,
} from "@impero-com/ui";
import chartLineSvg from "@impero-com/ui/img/chart-line.svg";
import { useNavigate } from "react-router-dom";

import { FrictionBreakdown } from "api/generated/model";

import * as theme from "./ControlHealthFrictionBreakdownWidget.css";
import { ControlHealthWidget } from "./ControlHealthWidget";
import {
    FrictionBreakdownData,
    prepareFrictionBreakdownData,
} from "./frictionBreakdownData";
import {
    FRICTION_BREAKDOWN_VIEWS,
    frictionBreakdownStorage,
    SupportedFrictionBreakdownView,
} from "./frictionBreakdownStorage";
import {
    FRICTION_BREAKDOWN_VIEW_DEFINITIONS,
    FrictionBreakdownViewDefinition,
} from "./frictionBreakdownViews";
import {
    CONTROL_HEALTH_PENDO,
    FRICTION_BAR_PENDO,
    FRICTION_TAB_PENDO,
} from "./pendo";
import { useBuildFrictionBucketUrl } from "./useBuildFrictionBucketUrl";

interface ControlHealthFrictionBreakdownWidgetProps {
    breakdown: FrictionBreakdown;
    rangeLabel: string;
}

export function ControlHealthFrictionBreakdownWidget({
    breakdown,
    rangeLabel,
}: ControlHealthFrictionBreakdownWidgetProps) {
    const { t } = useTranslation();
    const [view, setView] = useState(() =>
        frictionBreakdownStorage.getOr("frictionBreakdownView", "all")
    );
    const data = prepareFrictionBreakdownData(breakdown, view, rangeLabel, t);
    const definition = FRICTION_BREAKDOWN_VIEW_DEFINITIONS[view];
    const options = FRICTION_BREAKDOWN_VIEWS.map((value) => ({
        value,
        label: FRICTION_BREAKDOWN_VIEW_DEFINITIONS[value].label(t),
        "data-pendo": FRICTION_TAB_PENDO[value],
    }));

    const onChange = (value: SupportedFrictionBreakdownView) => {
        setView(value);
        frictionBreakdownStorage.set("frictionBreakdownView", value);
    };

    return (
        <ControlHealthWidget
            data-pendo={CONTROL_HEALTH_PENDO.frictionCard}
            collapsePendoId={CONTROL_HEALTH_PENDO.frictionCollapse}
            title={t("ControlHealth_FrictionBreakdown_Title")}
            info={t("ControlHealth_FrictionBreakdown_Info")}
            actions={
                <SegmentedControl
                    aria-label={t("ControlHealth_FrictionBreakdown_Title")}
                    options={options}
                    value={view}
                    onChange={onChange}
                />
            }
            headline={
                <FrictionBreakdownHeadline
                    data={data}
                    definition={definition}
                />
            }
            collapsible
            className={theme.card}
        >
            <FrictionBreakdownBody data={data} definition={definition} />
        </ControlHealthWidget>
    );
}

interface FrictionBreakdownBodyProps {
    data: FrictionBreakdownData;
    definition: FrictionBreakdownViewDefinition;
}

function FrictionBreakdownBody({
    data,
    definition,
}: FrictionBreakdownBodyProps) {
    const { t } = useTranslation();
    const navigate = useNavigate();
    const buildBucketUrl = useBuildFrictionBucketUrl(data.view);

    if (data.isEmpty) {
        return (
            <FrictionBreakdownPlaceholder
                title={definition.emptyTitle(t, data.range)}
                description={t(
                    "ControlHealth_FrictionBreakdown_Empty_Description"
                )}
            />
        );
    }

    const chartData: GroupedBarChartBucket[] = data.buckets.map((bucket) => ({
        label: bucket.label,
        values: { ...bucket.values },
    }));
    const spanByLabel = new Map(
        data.buckets.map(({ label, span }) => [label, span])
    );
    const series = definition.series(t).map((entry) => {
        const ids: Partial<Record<typeof entry.key, PendoId>> =
            FRICTION_BAR_PENDO[data.view];
        return {
            ...entry,
            "data-pendo": ids[entry.key],
        };
    });
    const totals: Record<string, number> = { ...data.totals };
    const Chart = data.view === "all" ? GroupedBarChart : StackedBarTrendChart;

    return (
        <div className={theme.body}>
            <Chart
                series={series}
                data={chartData}
                height={theme.PLOT_HEIGHT}
                onBarClick={(index, series) => {
                    const bucket = data.buckets[index];
                    if (bucket !== undefined) {
                        navigate(buildBucketUrl(bucket, series.key));
                    }
                }}
                renderTooltip={(bar) => (
                    <FrictionBreakdownTooltip
                        bar={bar}
                        span={
                            spanByLabel.get(bar.bucketLabel) ?? bar.bucketLabel
                        }
                    />
                )}
            />
            <div className={theme.xAxisTitle}>
                {t("ControlHealth_FrictionBreakdown_XAxisTitle")}
            </div>
            <ChartLegend
                layout="inline"
                heading={t("ControlHealth_FrictionBreakdown_EventsLabel")}
                items={series.map(({ key, label, color }) => ({
                    key,
                    label,
                    color,
                    value: totals[key],
                }))}
            />
        </div>
    );
}

function FrictionBreakdownHeadline({
    data,
    definition,
}: FrictionBreakdownBodyProps) {
    const { t } = useTranslation();

    return (
        <p className={theme.headline}>
            <span className={theme.headlineValue}>
                {data.total.toLocaleString()}
            </span>
            <span className={theme.headlineCaption}>
                {definition.headlineCaption(t, data.total, data.range)}
            </span>
        </p>
    );
}

interface FrictionBreakdownTooltipProps {
    bar: GroupedBarChartBar | StackedBarTrendChartBar;
    span: string;
}

function FrictionBreakdownTooltip({
    bar,
    span,
}: FrictionBreakdownTooltipProps) {
    return (
        <>
            <span className={theme.tooltipHeading}>{span}</span>
            <span className={theme.tooltipRow}>
                <ChartSwatch color={bar.series.color} ringed />
                <span>{bar.series.label}</span>
                <span className={theme.tooltipValue}>
                    {bar.value.toLocaleString()}
                </span>
            </span>
        </>
    );
}

interface FrictionBreakdownPlaceholderProps {
    title: string;
    description: string;
}

function FrictionBreakdownPlaceholder({
    title,
    description,
}: FrictionBreakdownPlaceholderProps) {
    return (
        <div className={theme.placeholder}>
            <EmptyState
                title={title}
                description={description}
                icon={<Svg src={chartLineSvg} />}
            />
        </div>
    );
}
